// visual_guard — le mesureur : demander une mesure à l'aperçu, et apparier la réponse.
//
// ── CE QU'IL RÉSOUT ────────────────────────────────────────────────────────
//
// `visual_check` orchestre et `visual_verdict` décide ; aucun des deux ne sait parler à une
// iframe. Ce module est le seul morceau qui le sache, et il est écrit pour être éprouvable sans
// navigateur : tout ce qui touche au DOM passe par `ports`.
//
// ── LE PROBLÈME CENTRAL : APPARIER ─────────────────────────────────────────
//
// On mesure trois largeurs l'une après l'autre. Les réponses arrivent par `postMessage`, donc de
// façon asynchrone et sans ordre garanti. Une réponse tardive de 360 px lue comme la mesure de
// 768 px ferait porter le verdict sur une page qui n'a jamais existé — et le refus comme
// l'acceptation seraient alors du hasard.
//
// D'où un identifiant par demande, et une table des demandes en attente. Tout ce qui n'y
// correspond pas est JETÉ, sans exception : une réponse en retard, une réponse en double, une
// réponse qu'on n'a pas demandée.
//
// ── ET CE QUI ARRIVE QUAND L'APERÇU NE RÉPOND PAS ──────────────────────────
//
// Il y a au moins trois raisons pour qu'il se taise, et aucune n'est la faute de l'opérateur :
// l'app a été déployée avant cette version et n'a pas de quoi répondre ; elle n'a pas fini de
// charger ; le navigateur BRIDE les trames d'animation d'une iframe hors écran, ce que seule la
// sonde (platform-ui/scripts/verify_visual.ts) peut dire.
//
// Dans tous les cas on abandonne au bout d'un délai et on rend `null`. L'orchestration traite une
// mesure absente comme une absence de preuve, donc elle accepte la retouche. C'est le repli
// assumé de tout le dispositif : on ne refuse jamais le geste de quelqu'un sur ce qu'on n'a pas
// su mesurer.
import { measureMessage } from "../../../frontend-unit-bundle/platform/src/_runtime/visual_bridge";
import type { Mesurer } from "./visual_check";
import type { WidthMeasure } from "./visual_verdict";

/**
 * Combien de temps on attend une mesure.
 *
 * L'app attend deux trames d'animation avant de mesurer, et une iframe hors écran peut les voir
 * bridées. Large, donc — mais pas infini : six mesures qui s'éternisent feraient attendre
 * l'enregistrement d'une retouche, et c'est le geste de l'opérateur qui en souffrirait.
 */
export const MEASURE_TIMEOUT_MS = 2_500;

/** Tout ce que ce module touche au monde extérieur. Injecté, donc éprouvable. */
export type GuardPorts = {
  /** Poser la largeur du cadre de mesure. */
  readonly setWidth: (px: number) => void;
  /** Envoyer au cadre de mesure, à l'origine exacte. */
  readonly post: (message: unknown) => void;
  /** Un minuteur, qui rend de quoi l'annuler. */
  readonly delai: (ms: number, f: () => void) => () => void;
};

export type Mesureur = {
  /** Ce que `visual_check` appelle. */
  readonly mesurer: Mesurer;
  /** À appeler pour chaque `measured` validé par le pont. */
  readonly recevoir: (id: number, page: WidthMeasure) => void;
  /** Tout abandonner — au démontage. Les demandes en attente rendent `null`.
   *
   *  Rend COMBIEN de demandes ont été abandonnées. Ce n'est pas pour les tests : c'est la seule
   *  façon pour un appelant de savoir que des mesures étaient encore en vol, et c'est aussi ce
   *  qui rend vérifiable qu'une demande résolue quitte bien la table — sans ce retrait, la table
   *  grossirait de six entrées par geste pour toute la session. */
  readonly fermer: () => number;
};

export function creerMesureur(ports: GuardPorts, timeoutMs: number = MEASURE_TIMEOUT_MS): Mesureur {
  /** Les demandes en attente, par identifiant. */
  const attentes = new Map<number, { resoudre: (m: WidthMeasure | null) => void; annuler: () => void }>();

  const terminer = (id: number, m: WidthMeasure | null): void => {
    const a = attentes.get(id);
    if (a === undefined) return;
    // RETIRÉE AVANT D'ÊTRE RÉSOLUE : sinon une seconde réponse du même identifiant résoudrait
    // une promesse déjà tenue — sans effet, mais le minuteur resterait, lui, en vie.
    attentes.delete(id);
    a.annuler();
    a.resoudre(m);
  };

  const mesurer: Mesurer = (doc, width, id) =>
    new Promise<WidthMeasure | null>((resoudre) => {
      const annuler = ports.delai(timeoutMs, () => terminer(id, null));
      attentes.set(id, { resoudre, annuler });
      // LA LARGEUR D'ABORD, LA DEMANDE ENSUITE — et un seul message.
      //
      // `setWidth` force la disposition du parent, donc la nouvelle taille du cadre est
      // propagée avant l'envoi. MESURÉ le 2026-10-06 (scripts/verify_offscreen_frame.ts) :
      // l'app rapporte alors exactement 360, 768 et 1280.
      //
      // Le document VOYAGE AVEC LA DEMANDE. Deux messages séparés obligeaient l'app à attendre
      // une trame d'animation entre les deux — et une iframe hors de l'écran n'en reçoit pas de
      // deuxième, donc aucune mesure ne revenait jamais et la garde acceptait tout en silence.
      //
      // Si malgré tout le cadre n'avait pas fini de se redimensionner, l'app rapporte sa propre
      // largeur et `visual_check` écarte la mesure. Deux gardes, pas une.
      ports.setWidth(width);
      ports.post(measureMessage(id, doc as never));
    });

  return {
    mesurer,
    // Une réponse qu'on n'attend pas — en retard, en double, jamais demandée — tombe ici et ne
    // fait rien : `terminer` ne trouve pas l'identifiant.
    recevoir: (id, page) => terminer(id, page),
    fermer: () => {
      const ids = [...attentes.keys()];
      for (const id of ids) terminer(id, null);
      return ids.length;
    },
  };
}
